import CT from '@marketplace-ai/tags';
import { ColDef, DoesFilterPassParams } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { Pill } from '~/components';
import { dateComparator } from '~/components/agGrid/columns/dateColumns';
import { createValidCol } from '~/components/agGrid/columns/sharedColumns';
import { CustomDateRangeFilter, CustomSetFilter } from '~/components/agGrid/filters';
import { ACTIONS_COLUMN_BASE, ActionsRenderer, type ActionDef } from '~/components/Table/gridUtil';
import { formatCredentialDate } from '~/features/utils';
import { EMPTY_ADDRESS_FORM } from '../../../constants';
import { FacilityRow } from './FacilityRow';
import { EmploymentFormValues } from './types';

export const emptyEmploymentForm: EmploymentFormValues = {
  apptType: { label: '', value: '' },
  currentFlag: false,
  department: '',
  employer: '',
  endDate: '',
  facility: { label: '', value: '', userAdded: false, address: EMPTY_ADDRESS_FORM },
  leaveReason: '',
  privilegesHeldCheckbox: false,
  rehireEligibleComment: '',
  rehireToggle: false,
  startDate: '',
  title: '',
};

export const APPOINTMENT_TYPES = [
  'Attending',
  'Affiliate',
  'Consulting',
  'Courtesy',
  'Temporary',
  'Emeritus',
  'Contracted',
  'Physician',
  'Scholar/Limited',
  'Provisional',
];

export const APPOINTMENT_OPTIONS = APPOINTMENT_TYPES.map((type) => ({ value: type, label: type }));

export const CreateEmploymentColumns = ({
  handleDelete,
  handleEdit,
}: {
  handleDelete: (id: number) => void;
  handleEdit: (id: number) => void;
}): ColDef<EmploymentFormValues>[] => [
  { field: 'id', hide: true },
  createValidCol(),
  {
    headerName: 'Employer',
    cellStyle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      textAlign: 'left',
      paddingTop: '10px',
      paddingBottom: '12px',
      lineHeight: '1.5',
    },
    cellRenderer: ({ data }: CustomCellRendererProps) => {
      return (
        <FacilityRow
          employer={data.employer}
          address={data.facility?.address}
          location={data.location}
        />
      );
    },
    valueGetter: ({ data }) => data?.employer ?? '',
  },
  {
    headerName: 'Dates',
    cellRenderer: ({ data }: CustomCellRendererProps) => {
      return (
        <>
          {formatCredentialDate(data.startDate)} –{' '}
          {data.endDate ? formatCredentialDate(data.endDate) : 'Present'}
        </>
      );
    },
    valueGetter: ({ data }) => {
      return data ? new Date(data.startDate) : new Date(0);
    },
    // null endDate = ongoing job: treated as active indefinitely for overlap checks.
    filter: {
      component: CustomDateRangeFilter,
      doesFilterPass: ({ model, node }: DoesFilterPassParams) => {
        if (!model || (!model.startDate && !model.endDate)) return true;
        const start = node.data?.startDate ? new Date(node.data.startDate) : null;
        const end = node.data?.endDate ? new Date(node.data.endDate) : null;
        const from = model.startDate ? new Date(model.startDate) : null;
        const to = model.endDate ? new Date(model.endDate) : null;
        if (from && end && end < from) return false;
        if (to && start && start > to) return false;
        return true;
      },
    },
    comparator: dateComparator,
  },
  {
    headerName: 'Position',
    cellStyle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      textAlign: 'left',
      paddingTop: '10px',
      paddingBottom: '12px',
      lineHeight: '1.5',
    },
    cellRenderer: ({ data }: CustomCellRendererProps) => {
      const apptType = data.apptType?.label ?? '';
      const department = data.department ?? '';
      const secondary =
        [apptType, department].filter(Boolean).join(' - ') || 'Department Not Specified';
      return (
        <div className="w-full text-left leading-tight">
          <div>{data.title || 'Title Not Specified'}</div>
          <div>{secondary}</div>
        </div>
      );
    },
    valueGetter: ({ data }) => data?.title ?? '',
    filterValueGetter: ({ data }) => {
      if (!data) return '';
      return [data.title, data.apptType?.label, data.department].filter(Boolean).join(' ');
    },
  },
  {
    headerName: 'Current',
    cellRenderer: ({ data }: CustomCellRendererProps) => {
      return (
        <Pill
          label={data.endDate ? 'Previous' : 'Current Job'}
          customStyle="bg-white text-black ring-grey-300/20"
        />
      );
    },
    valueGetter: ({ data }) => (data?.endDate ? 'Previous' : 'Current Job'),
    filter: {
      component: CustomSetFilter,
      doesFilterPass: ({ model, node }: DoesFilterPassParams) => {
        if (!model || model.length === 0) return true;
        const label = node.data?.endDate ? 'Previous' : 'Current Job';
        return model.includes(label);
      },
    },
  },
  {
    ...ACTIONS_COLUMN_BASE,
    width: 80,
    cellRenderer: ActionsRenderer,
    cellRendererParams: {
      actions: [
        { type: 'edit', handler: handleEdit, cyTag: CT.employment.table.editBtn },
        { type: 'delete', handler: handleDelete, cyTag: CT.employment.table.deleteBtn },
      ] satisfies ActionDef[],
    },
  },
];
